import { useState } from "react";
import { Check, Copy, Flame, Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useBurnBankVariant, useGetMe } from "@workspace/api-client-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { showBurnSummary } from "@/lib/burn-summary";

export function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard?.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
      className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
      title="Copy"
    >
      {copied ? (
        <Check className="h-3 w-3 text-green-500" />
      ) : (
        <Copy className="h-3 w-3" />
      )}
    </button>
  );
}

function NumberRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <div className="flex items-center gap-1.5">
        <span className="font-mono text-xs text-foreground tabular-nums">
          {value || "—"}
        </span>
        {value && <CopyButton value={value} />}
      </div>
    </div>
  );
}

export interface BankDetails {
  label: string;
  variant?: string | null;
  routingNumber?: string | null;
  accountNumber?: string | null;
  routingLast4?: string | null;
  accountLast4?: string | null;
  // When set (and mappingStatus is "assigned"), the popover offers a "Burn
  // slot" action that retires this variant and reassigns the account.
  variantId?: number | null;
  mappingStatus?: string | null;
  accountEmail?: string | null;
}

// Confirmation dialog mirroring the Banking page's BurnVariantDialog: shows the
// reference TM error image and a plain-English warning before retiring a slot.
function BurnSlotDialog({
  open,
  onOpenChange,
  details,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  details: BankDetails;
}) {
  const queryClient = useQueryClient();
  const burn = useBurnBankVariant({
    mutation: {
      onSuccess: (res) => {
        onOpenChange(false);
        showBurnSummary(res);
        // The burn changes bank assignment across every inventory/account
        // surface that renders this shared popover, so refresh broadly.
        queryClient.invalidateQueries();
      },
      onError: () => toast.error("Failed to burn bank account"),
    },
  });

  const slotLabel = details.variant
    ? `${details.label} ${details.variant}`
    : details.label;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Burn bank account {details.label}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <img
            src={`${import.meta.env.BASE_URL}burned-tm-error.png`}
            alt="Ticketmaster duplicate-account error"
            className="w-full rounded border border-border"
          />
          <p className="text-xs text-muted-foreground">
            Confirm you have seen the Ticketmaster error shown above before
            proceeding. Ticketmaster rejects the whole routing number, so this
            retires <span className="font-medium text-foreground">every slot</span> on
            this bank account and moves the accounts on it to{" "}
            <span className="font-medium text-foreground">new bank accounts</span>.
          </p>
          <p className="text-[11px] text-muted-foreground">
            Any account that is already listed or sold is left in place so its
            listing/sale link is preserved. Every burned slot keeps its account
            link for future transaction matching.
          </p>
        </div>
        <DialogFooter>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={burn.isPending}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            className="bg-red-600 hover:bg-red-700 text-white"
            disabled={burn.isPending || details.variantId == null}
            onClick={() =>
              details.variantId != null &&
              burn.mutate({ variantId: details.variantId })
            }
          >
            {burn.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
            ) : (
              <Flame className="h-3.5 w-3.5 mr-1" />
            )}
            Confirm &amp; Burn Account
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Compact bank cell for dense tables: shows card name + variant inline, and
// reveals the FULL routing/account numbers (with copy) in a click popover.
// Operators (and admins) additionally get a "Burn account" action when an
// assigned variant is known, so a rejected slot can be burned without leaving
// the inventory view.
export function BankNumbersPopover({ details }: { details: BankDetails }) {
  const routing = details.routingNumber ?? null;
  const account = details.accountNumber ?? null;
  const last4 = details.accountLast4 ? `••${details.accountLast4}` : null;
  const [burnOpen, setBurnOpen] = useState(false);

  const { data: me } = useGetMe();
  const canBurn =
    (me?.role === "admin" || me?.role === "operator") &&
    details.variantId != null &&
    details.mappingStatus === "assigned";

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-1.5 min-w-0 text-left hover:underline decoration-dotted underline-offset-2"
          >
            <span
              className="text-xs text-secondary-foreground truncate max-w-[140px]"
              title={details.label}
            >
              {details.label}
            </span>
            {details.variant && (
              <span className="font-mono text-[10px] text-muted-foreground">
                {details.variant}
              </span>
            )}
            {last4 && (
              <span className="font-mono text-[10px] text-muted-foreground">
                {last4}
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-3 space-y-2">
          <div className="text-xs font-medium text-foreground">
            {details.label}
            {details.variant && (
              <span className="ml-1.5 font-mono text-[10px] text-muted-foreground">
                {details.variant}
              </span>
            )}
          </div>
          <div className="space-y-1.5 border-t border-border pt-2">
            <NumberRow label="Routing" value={routing} />
            <NumberRow label="Account" value={account} />
          </div>
          {canBurn && (
            <div className="border-t border-border pt-2">
              <Button
                size="sm"
                variant="outline"
                className="h-6 w-full px-2 text-[10px] border-red-500/40 text-red-500 hover:bg-red-500/10 hover:text-red-500"
                onClick={(e) => {
                  e.stopPropagation();
                  setBurnOpen(true);
                }}
              >
                <Flame className="h-3 w-3 mr-0.5" />
                Burn account
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
      {canBurn && (
        <BurnSlotDialog
          open={burnOpen}
          onOpenChange={setBurnOpen}
          details={details}
        />
      )}
    </>
  );
}
